import { Mutation } from "@apollo/client/react/components";
import FundamenteiWithText from "components/branding/FundamenteiWithText";
import LogoSizingProps from "components/branding/LogoSizingProps";
import MailTo from "components/errors/MailTo";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import Link from "next/link";
import { FormattedMessage, useIntl } from "react-intl";
import Footer from "../components/auth/Footer";
import ForgotPasswordForm from "../components/auth/ForgotPasswordForm";
import { useAmplitude } from "../lib/amplitude/Amplitude";
import ForgotPasswordMutation from "../lib/queries/auth/ForgotPassword";
import type {
  IForgotPasswordMutation,
  IForgotPasswordMutationVariables,
} from "../lib/queries/auth/__generated__/ForgotPassword.generated";

export default function ForgotPassword() {
  const intl = useIntl();
  const amplitude = useAmplitude();

  return (
    <div className="mx-auto grid h-screen max-w-screen-legacyDesktop grid-cols-3 gap-x-8 sm:grid-cols-8 lg:grid-cols-12">
      <FundamenteiMetadata
        title={intl.formatMessage({ defaultMessage: "Recuperar senha | Fundamentei", id: "Umht3Y" })}
      />

      <div className="col-span-3 w-full py-10 sm:col-span-6 sm:col-start-2 md:col-span-4 md:col-start-3 lg:col-span-4 lg:col-start-5">
        <div className="grid gap-y-7">
          <div className="text-center">
            <Link href="/" title="Fundamentei" className="flex justify-center text-center">
              <FundamenteiWithText
                {...LogoSizingProps.FundamenteiWithText.MEDIUM}
                style={{ display: "inline-flex !important" }}
              />
            </Link>
          </div>

          <div className="grid gap-y-5 px-6 sm:rounded sm:bg-white sm:py-6 sm:pb-7 sm:shadow-lg">
            <div className="grid gap-y-1 text-center sm:text-left">
              <h1 className="m-0 text-xl font-bold tracking-wider text-gray-700">
                <FormattedMessage
                  description="Página de recuperação de senha"
                  defaultMessage="Esqueceu sua senha?"
                  id="OoV7/s"
                />
              </h1>
            </div>

            <div className="grid gap-y-5">
              <Mutation<IForgotPasswordMutation, IForgotPasswordMutationVariables>
                mutation={ForgotPasswordMutation}
                onCompleted={() => {
                  amplitude.logEvent("Forgot password");
                }}
              >
                {(forgotPassword, { called, data }) => (
                  <>
                    <ForgotPasswordForm
                      onSubmit={async ({ email, turnstileToken, turnstileIdempotencyKey }) => {
                        await forgotPassword({
                          variables: {
                            email,
                            turnstileToken,
                            turnstileIdempotencyKey,
                          },
                        });

                        return null;
                      }}
                    />

                    {called && data && data.forgotPassword && (
                      <div className="-mx-6 flex flex-col gap-y-3 px-6">
                        <p className="m-0 text-sm font-medium tracking-wider text-emerald-700">
                          <FormattedMessage
                            description="Página de recuperação de senha"
                            defaultMessage="Tudo certo! Um email com instruções para redefinição de senha foi enviado para você."
                            id="Qixz1d"
                          />
                        </p>
                        <p className="m-0 text-sm font-medium tracking-wider text-slate-700">
                          <FormattedMessage
                            description="Página de recuperação de senha"
                            defaultMessage="Caso não receba o e-mail de recuperação em alguns minutos, entre em contato pelo suporte: {mail}."
                            id="0dW8+z"
                            values={{
                              mail: <MailTo address="contato@fundamentei.com" />,
                            }}
                          />
                        </p>
                      </div>
                    )}
                  </>
                )}
              </Mutation>

              <div className="text-center">
                <Link
                  href="/login"
                  passHref={true}
                  className="text-sm font-medium uppercase tracking-wide text-blue-600 no-underline hover:underline"
                >
                  <FormattedMessage description="Página de recuperação de senha" defaultMessage="Voltar" id="iMjP+e" />
                </Link>
              </div>

              <p className="m-0 text-center text-sm tracking-wide text-lynch">
                {"Ainda não tem uma conta? "}
                <Link
                  href="/sign-up"
                  passHref={true}
                  className="ml-auto text-sm font-medium tracking-wide text-blue-600 no-underline hover:underline"
                >
                  <FormattedMessage
                    description="Página de recuperação de senha"
                    defaultMessage="Cadastre-se!"
                    id="OzzZ+f"
                  />
                </Link>
              </p>
            </div>
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
}
